import React, { useState, useRef } from 'react';
import { Phone, Mail, Send, MessageCircle, UploadCloud, FileText, X, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FINALIDADES = [
  { value: 'heranca', label: 'Partilha de Herança' },
  { value: 'divorcio', label: 'Divórcio / Separação' },
  { value: 'credito', label: 'Crédito Habitação' },
  { value: 'compra_venda', label: 'Compra / Venda' },
  { value: 'judicial', label: 'Processo Judicial' },
  { value: 'contabilistico', label: 'Fins Contabilísticos' },
  { value: 'outro', label: 'Outro' }
];

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const formatSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preferredContact: 'whatsapp',
  finalidade: '',
  propertyDescription: ''
};

export const ContactForm = ({ title, submitLabel, whatsAppMessage, idPrefix = '', containerStyle }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const file of incoming) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        toast.error(`Formato não permitido: ${file.name}. Use PDF, JPG ou PNG.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} excede o limite de 10MB.`);
        continue;
      }
      accepted.push(file);
    }
    setFiles(prev => {
      const merged = [...prev, ...accepted];
      if (merged.length > MAX_FILES) {
        toast.error(`Máximo de ${MAX_FILES} ficheiros.`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => payload.append(key, value));
      files.forEach(file => payload.append('files', file));

      const response = await axios.post(`${API}/contact`, payload);

      if (response.data) {
        toast.success(
          files.length > 0
            ? 'Pedido e documentos enviados com sucesso! Entraremos em contacto em breve.'
            : 'Pedido enviado com sucesso! Entraremos em contacto em breve.'
        );
        setFormData(INITIAL_FORM);
        setFiles([]);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Erro ao enviar pedido. Por favor, tente novamente ou contacte-nos directamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(whatsAppMessage);
    window.open(`https://wa.me/351915964080?text=${message}`, '_blank');
  };

  return (
    <div className="valuare-form-container" style={containerStyle}>
      <h3 className="valuare-form-title">{title}</h3>
      <form onSubmit={handleSubmit} className="valuare-form">
        <div className="valuare-form-row">
          <div className="valuare-form-field">
            <label htmlFor={`${idPrefix}firstName`}>Primeiro Nome</label>
            <Input
              id={`${idPrefix}firstName`}
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              placeholder="João"
            />
          </div>
          <div className="valuare-form-field">
            <label htmlFor={`${idPrefix}lastName`}>Último Nome</label>
            <Input
              id={`${idPrefix}lastName`}
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              placeholder="Silva"
            />
          </div>
        </div>
        <div className="valuare-form-row">
          <div className="valuare-form-field">
            <label htmlFor={`${idPrefix}email`}>Email</label>
            <Input
              id={`${idPrefix}email`}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="joao.silva@exemplo.com"
            />
          </div>
          <div className="valuare-form-field">
            <label htmlFor={`${idPrefix}phone`}>Telemóvel</label>
            <Input
              id={`${idPrefix}phone`}
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              required
              placeholder="+351 912 345 678"
            />
          </div>
        </div>

        <div className="valuare-form-field">
          <label htmlFor={`${idPrefix}finalidade`}>
            Finalidade da Avaliação <span className="valuare-optional-tag">Opcional</span>
          </label>
          <select
            id={`${idPrefix}finalidade`}
            name="finalidade"
            className="valuare-form-select"
            value={formData.finalidade}
            onChange={handleInputChange}
            data-testid={`${idPrefix}contact-finalidade-select`}
          >
            <option value="">Selecione a finalidade</option>
            {FINALIDADES.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <div className="valuare-form-field">
          <label htmlFor={`${idPrefix}propertyDescription`}>
            Breve Descrição do Imóvel <span className="valuare-optional-tag">Opcional</span>
          </label>
          <textarea
            id={`${idPrefix}propertyDescription`}
            name="propertyDescription"
            className="valuare-form-textarea"
            value={formData.propertyDescription}
            onChange={handleInputChange}
            maxLength={2000}
            rows={4}
            placeholder="Ex.: Apartamento T3 em Almada, 110 m², bom estado de conservação, com garagem..."
            data-testid={`${idPrefix}contact-description-textarea`}
          />
        </div>

        <div className="valuare-upload" data-testid={`${idPrefix}contact-upload-group`}>
          <label className="valuare-upload-label">
            Documentos do Imóvel <span className="valuare-optional-tag">Opcional</span>
          </label>
          <span className="valuare-upload-boost">
            <Zap />
            Anexe a Caderneta Predial ou outros documentos e receba o seu orçamento mais rapidamente
          </span>
          <div
            className={`valuare-upload-dropzone ${dragActive ? 'is-dragging' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click(); }}
            data-testid={`${idPrefix}contact-upload-dropzone`}
          >
            <UploadCloud className="valuare-upload-icon" />
            <p>Arraste ficheiros para aqui ou <u>clique para escolher</u></p>
            <span className="valuare-upload-formats">PDF, JPG, PNG · máx. 5 ficheiros · 10MB cada</span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              style={{ display: 'none' }}
              data-testid={`${idPrefix}contact-file-input`}
            />
          </div>
          {files.length > 0 && (
            <ul className="valuare-upload-list" data-testid={`${idPrefix}contact-file-list`}>
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} className="valuare-upload-item" data-testid={`${idPrefix}contact-file-item-${index}`}>
                  <FileText />
                  <span className="valuare-upload-item-name">{file.name}</span>
                  <span className="valuare-upload-item-size">{formatSize(file.size)}</span>
                  <button
                    type="button"
                    className="valuare-upload-item-remove"
                    onClick={() => removeFile(index)}
                    aria-label={`Remover ${file.name}`}
                    data-testid={`${idPrefix}contact-file-remove-${index}`}
                  >
                    <X />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="valuare-contact-pref" data-testid={`${idPrefix}preferred-contact-group`}>
          <label className="valuare-contact-pref-label" htmlFor={`${idPrefix}preferred-whatsapp`}>
            Como Prefere Ser Contactado?
          </label>
          <span className="valuare-contact-pref-hint">Escolha o meio mais conveniente para si</span>
          <div className="valuare-contact-pref-options" role="radiogroup" aria-label="Meio de contacto preferencial">
            {[
              { value: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle /> },
              { value: 'email', label: 'Email', icon: <Mail /> },
              { value: 'phone', label: 'Telefonema', icon: <Phone /> }
            ].map((opt) => {
              const selected = formData.preferredContact === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={`${idPrefix}preferred-${opt.value}`}
                  className={`valuare-contact-pref-option ${selected ? 'is-selected' : ''}`}
                  data-testid={`${idPrefix}preferred-contact-${opt.value}`}
                >
                  <input
                    type="radio"
                    id={`${idPrefix}preferred-${opt.value}`}
                    name="preferredContact"
                    value={opt.value}
                    checked={selected}
                    onChange={handleInputChange}
                  />
                  {opt.icon}
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="valuare-form-actions">
          <Button type="submit" disabled={isSubmitting} className="valuare-btn-submit" data-testid={`${idPrefix}contact-submit-button`}>
            <Send className="valuare-btn-icon" />
            {isSubmitting ? 'A enviar...' : submitLabel}
          </Button>
          <Button type="button" onClick={handleWhatsAppClick} variant="outline" className="valuare-btn-whatsapp">
            <Phone className="valuare-btn-icon" />
            WhatsApp
          </Button>
        </div>
      </form>
    </div>
  );
};
